import { HttpClient } from '@adapters/HttpClient';
import {
  Incident,
  IncidentFilters,
  IncidentActionsResponse,
  IncidentHistory,
  SolveIncidentPayload,
  ManagementPayload,
  CourierResponsePayload,
  ResolveByCourierPayload,
  IncidentDetailResponse,
} from '../models/Incident';
import { INCIDENT_LIST_STATUS_TAB_ORDER } from '../models/Filters';

export interface IncidentsPage {
  data: Incident[];
  meta: {
    currentPage: number;
    lastPage: number;
    total: number;
    perPage: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export class IncidentsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Construye query params de filtros compartidos entre listado y count-by-status.
   */
  private buildListQueryParams(filters: IncidentFilters = {}): Record<string, any> {
    const { status, client_id, mapped, from, to, search, include_resolved_by_courier, advanced_filters } = filters;

    const params: Record<string, any> = {};

    if (include_resolved_by_courier !== undefined) {
      params.include_resolved_by_courier = include_resolved_by_courier;
    }

    if (advanced_filters && Array.isArray(advanced_filters.filters)) {
      advanced_filters.filters.forEach((f) => {
        const { column, operator, value } = f;

        switch (column) {
          case 'status':
            if (operator === 'equals' && value) {
              if (params.status) {
                params.status = `${params.status},${value}`;
              } else {
                params.status = value;
              }
            } else if (operator === 'in' && Array.isArray(value) && value.length > 0) {
              if (params.status) {
                params.status = `${params.status},${value.join(',')}`;
              } else {
                params.status = value.join(',');
              }
            }
            break;

          case 'courier_id':
            if (operator === 'in' && Array.isArray(value) && value.length > 0) {
              params.courier_id = value.join(',');
            } else if (operator === 'equals' && value) {
              params.courier_id = parseInt(value, 10);
            }
            break;

          case 'shop_id':
            if (operator === 'in' && Array.isArray(value) && value.length > 0) {
              params.shop_id = parseInt(value[0], 10);
            } else if (operator === 'equals' && value) {
              params.shop_id = parseInt(value, 10);
            }
            break;

          case 'team_id':
            if (operator === 'in' && Array.isArray(value) && value.length > 0) {
              params.team_id = parseInt(value[0], 10);
            } else if (operator === 'equals' && value) {
              params.team_id = parseInt(value, 10);
            }
            break;

          case 'client_id':
            if (operator === 'equals' && value) {
              params.client_id = parseInt(value, 10);
            }
            break;

          case 'order_id':
          case 'external_code':
            if (operator === 'equals' && value) {
              if (!params.search) {
                params.search = String(value);
              }
            } else if (operator === 'contains' && value) {
              if (!params.search) {
                params.search = String(value);
              }
            }
            break;

          case 'report_date':
            if (operator === 'equals' && value) {
              if (!params.from) {
                params.from = value;
              }
              if (!params.to) {
                params.to = value;
              }
            } else if (operator === 'greater_than' && value) {
              params.from = value;
            } else if (operator === 'less_than' && value) {
              params.to = value;
            } else if (operator === 'in' && Array.isArray(value) && value.length >= 2) {
              params.from = value[0];
              params.to = value[1];
            }
            break;

          case 'resolution_date':
            if (operator === 'equals' && value) {
              // La API no expone parámetro específico para resolution_date
            }
            break;

          default:
            break;
        }
      });
    }

    if (status && status.length > 0) {
      params.status = status.join(',');
    }

    if (filters.courier_ids) {
      params.courier_ids = filters.courier_ids;
    }

    if (client_id) {
      params.client_id = client_id;
    }

    if (filters.shop_ids) {
      params.shop_ids = filters.shop_ids;
    }

    if (filters.team_ids) {
      params.team_ids = filters.team_ids;
    }

    if (mapped !== undefined) {
      params.mapped = mapped;
    }

    if (from) {
      params.from = from;
    }

    if (to) {
      params.to = to;
    }

    if (search) {
      params.search = search.substring(0, 255);
    }

    return params;
  }

  /**
   * Totales por estado respetando los mismos filtros que el listado (sin paginación ni orden).
   */
  async getIncidentsCountByStatus(filters: IncidentFilters = {}): Promise<Record<string, number>> {
    const params = this.buildListQueryParams(filters);

    const response = await this.httpClient.get<{ data: { status: string; count: number }[] }>(
      '/incidents/count-by-status',
      params,
      {},
      false,
    );

    const items = Array.isArray(response?.data) ? response.data : [];
    const counts: Record<string, number> = {};
    INCIDENT_LIST_STATUS_TAB_ORDER.forEach((status) => {
      counts[status] = 0;
    });
    items.forEach(({ status, count }) => {
      if (status) {
        counts[status] = count ?? 0;
      }
    });

    counts.all = INCIDENT_LIST_STATUS_TAB_ORDER.reduce((sum, status) => sum + (counts[status] ?? 0), 0);

    return counts;
  }

  /**
   * Obtiene el listado paginado de incidencias
   */
  async getIncidents(filters: IncidentFilters = {}): Promise<IncidentsPage> {
    const { page = 1, per_page = 25, sortBy = 'id', sortDirection = 'desc' } = filters;

    const params: Record<string, any> = {
      ...this.buildListQueryParams(filters),
      page: Math.max(1, page),
      per_page: Math.min(Math.max(1, per_page), 100),
    };

    if (sortBy) {
      params.order_by = sortBy;
    }
    if (sortDirection) {
      params.order_direction = sortDirection;
    }

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/incidents', params, {}, showGlobalLoading);

    const rawData = Array.isArray(response?.data) ? response.data : [];

    // Mapear los datos de la API al formato esperado por el modelo
    const data: Incident[] = rawData.map((item: any) => {
      const order = item.order;
      const shop = order?.shop;

      const courier = item.courier
        ? {
            id: item.courier.id,
            name: item.courier.name,
            // Normalizamos el nuevo campo `logo` al formato esperado por los componentes actuales.
            image_hash: item.courier.image_hash ?? item.courier.logo ?? null,
            logo: item.courier.logo ?? null,
          }
        : undefined;

      return {
        id: item.id,
        order_id: item.order_id,
        order_ref: order?.ref ?? null,
        shop_name: shop?.name ?? null,
        shop_team_name: shop?.team?.name ?? null,
        tracking_number: order?.tracking_number ?? null,
        courier_id: item.courier_id ?? item.courier?.id,
        courier,
        // Mapear courier_incident_kind a external_code y external_label
        external_code: item.courier_incident_kind?.external_code ?? '',
        external_label: item.courier_incident_kind?.external_label ?? '',
        incident_type_id: item.incident_type?.id || null,
        incident_type: item.incident_type
          ? {
              id: item.incident_type.id,
              code: item.incident_type.code,
              name: item.incident_type.code, // Usar code como name si no viene
              label: item.incident_type.label,
            }
          : null,
        status: item.status,
        report_date: item.report_date,
        resolution_date: item.resolution_date || null,
        resolved_by_courier: item.resolved_by_courier || false,
        management: item.management || null,
        standardized_description: item.standardized_description || null,
        created_at: item.created_at || item.report_date,
        updated_at: item.updated_at || item.report_date,
      };
    });

    const meta = response?.meta ?? {
      currentPage: 1,
      lastPage: 1,
      total: 0,
      perPage: per_page,
    };

    return {
      data,
      meta,
    };
  }

  /**
   * Obtiene el detalle de una incidencia
   * La respuesta incluye incidencia, historial y acciones_elegibles
   */
  async getIncidentById(id: number): Promise<IncidentDetailResponse> {
    const response = await this.httpClient.get<any>(`/incidents/${id}`);

    // La API devuelve { incidencia, historial, acciones_elegibles }
    return {
      incidencia: response.incidencia || response,
      historial: response.historial,
      acciones_elegibles: response.acciones_elegibles,
    };
  }

  /**
   * Obtiene las acciones elegibles para una incidencia
   */
  async getIncidentActions(id: number): Promise<IncidentActionsResponse> {
    const response = await this.httpClient.get<IncidentActionsResponse>(`/incidents/${id}/actions`);

    return response;
  }

  /**
   * Resuelve una incidencia ejecutando una acción
   */
  async solveIncident(
    id: number,
    payload: SolveIncidentPayload,
    idempotencyKey: string,
  ): Promise<ApiResponse<Incident>> {
    const response = await this.httpClient.put<ApiResponse<Incident>>(`/incidents/${id}/solve`, payload, false, {
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Marca una incidencia en gestión manual
   */
  async markInManagement(id: number, payload: ManagementPayload): Promise<ApiResponse<Incident>> {
    const response = await this.httpClient.put<ApiResponse<Incident>>(`/incidents/${id}/management`, payload);

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Registra la respuesta del courier
   */
  async registerCourierResponse(id: number, payload: CourierResponsePayload): Promise<ApiResponse<Incident>> {
    const response = await this.httpClient.post<ApiResponse<Incident>>(`/incidents/${id}/courier-response`, payload);

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Resuelve una incidencia por courier (atajo)
   */
  async resolveByCourier(id: number, payload: ResolveByCourierPayload): Promise<ApiResponse<Incident>> {
    const response = await this.httpClient.post<ApiResponse<Incident>>(`/incidents/${id}/resolve-by-courier`, payload);

    return {
      success: response?.success ?? true,
      message: response?.message,
      data: response?.data,
    };
  }

  /**
   * Obtiene el historial de una incidencia
   */
  async getIncidentHistory(id: number): Promise<IncidentHistory[]> {
    const response = await this.httpClient.get<IncidentHistory[]>(`/incidents/${id}/history`);

    const history = Array.isArray(response) ? response : (response?.data ?? []);
    return history;
  }
}
